import React, { useState, useEffect } from 'react';
import { WifiOff, Gamepad2, ArrowLeft, Trophy } from 'lucide-react';
import { GravityRunner } from './GravityRunner';
import { PetAlchemy } from './PetAlchemy';
import { PawRicochet } from './PawRicochet';

export const OfflineArcade: React.FC = () => {
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [selectedGame, setSelectedGame] = useState<string | null>(null);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Also Allow Manual Trigger for Testing (Optional, via console or state injection if needed)
    // For now, relies on navigator.onLine

    if (!isOffline) return null;

    if (selectedGame) {
        return (
            <div className="fixed inset-0 z-[9999] bg-black text-white flex flex-col">
                <button
                    onClick={() => setSelectedGame(null)}
                    className="absolute top-4 left-4 z-50 p-3 bg-white/10 rounded-full backdrop-blur-md hover:bg-white/20 transition-all border border-white/10"
                >
                    <ArrowLeft size={24} />
                </button>
                {selectedGame === 'gravity' && <GravityRunner />}
                {selectedGame === 'alchemy' && <PetAlchemy />}
                {selectedGame === 'ricochet' && <PawRicochet />}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[9000] bg-slate-950 text-white flex flex-col items-center justify-center p-6 animate-in fade-in duration-500">
            <div className="p-6 bg-slate-900/50 rounded-full mb-6 border border-slate-800 animate-pulse">
                <WifiOff size={64} className="text-slate-500" />
            </div>

            <h1 className="text-3xl font-black uppercase text-center mb-2 tracking-tighter">¡Sin Conexión!</h1>
            <p className="text-slate-400 text-center mb-10 max-w-xs font-medium">
                Parece que perdiste la señal. Mientras vuelve, ¡diviértete con nuestros juegos del Arcade Peludo!
            </p>

            <div className="grid grid-cols-1 gap-4 w-full max-w-md">
                <GameCard
                    title="Gravity Pup"
                    desc="Runner Infinito. Toca para cambiar la gravedad."
                    icon="🐕"
                    color="bg-gradient-to-r from-violet-600 to-indigo-600"
                    onClick={() => setSelectedGame('gravity')}
                />
                <GameCard
                    title="Alquimia Express"
                    desc="Fusiona juguetes y premios (2048 Style)."
                    icon="🦴"
                    color="bg-gradient-to-r from-amber-500 to-orange-600"
                    onClick={() => setSelectedGame('alchemy')}
                />
                <GameCard
                    title="Rebote Neón"
                    desc="Rompe bloques defendiendo la base."
                    icon="🎾"
                    color="bg-gradient-to-r from-pink-500 to-rose-600"
                    onClick={() => setSelectedGame('ricochet')}
                />
            </div>

            <p className="fixed bottom-6 text-[10px] text-slate-600 font-mono uppercase tracking-widest">
                Modo Offline • Amigo Peludo v2.0
            </p>
        </div>
    );
};

const GameCard = ({ title, desc, icon, color, onClick }: any) => (
    <button
        onClick={onClick}
        className={`w-full p-5 relative overflow-hidden group rounded-2xl ${color} hover:brightness-110 active:scale-95 transition-all text-left flex items-center gap-4 shadow-xl border border-white/10`}
    >
        <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-4 -translate-y-2 group-hover:scale-110 transition-transform">
            <Gamepad2 size={100} />
        </div>
        <div className="text-4xl relative z-10 bg-black/20 p-3 rounded-xl backdrop-blur-sm border border-white/10">{icon}</div>
        <div className="relative z-10">
            <h3 className="font-black text-lg uppercase tracking-tight">{title}</h3>
            <p className="text-xs font-medium opacity-90 leading-tight pr-8 text-white/80">{desc}</p>
        </div>
        <Gamepad2 className="ml-auto opacity-50 relative z-10" />
    </button>
);
